import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ScatterChart, Scatter, Area, AreaChart, ComposedChart } from 'recharts';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import PowerLawChartsSubtab from './PowerLawChartsSubtab';
import RiskHeatmapSubtab from './RiskHeatmapSubtab';
import ScenarioExplorerSubtab from './ScenarioExplorerSubtab';

/**
 * Extreme Events Results Visualization Component
 * Phase 2: Comparative results dashboard with advanced visualizations
 */
const ExtremeEventsResultsSubtab = ({ 
    simulationResults, 
    baselineResults, 
    isRunning,
    isDarkMode 
}) => {
    const [activeView, setActiveView] = useState('comparison');
    const [selectedMetric, setSelectedMetric] = useState('projectNPV');
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Process simulation results for visualization
    const processedResults = useMemo(() => {
        if (!simulationResults?.scenarios || simulationResults.scenarios.length === 0) {
            return null;
        }

        const scenarios = simulationResults.scenarios;
        const extremeEventScenarios = scenarios.filter(s => s.hasExtremeEvent);
        const normalScenarios = scenarios.filter(s => !s.hasExtremeEvent);

        // Calculate distribution statistics
        const calculateStats = (values) => {
            if (values.length === 0) return null;
            
            const sorted = values.sort((a, b) => a - b);
            return {
                mean: values.reduce((sum, val) => sum + val, 0) / values.length,
                median: sorted[Math.floor(sorted.length / 2)],
                p5: sorted[Math.floor(sorted.length * 0.05)],
                p25: sorted[Math.floor(sorted.length * 0.25)],
                p75: sorted[Math.floor(sorted.length * 0.75)],
                p95: sorted[Math.floor(sorted.length * 0.95)],
                min: Math.min(...values),
                max: Math.max(...values),
                std: Math.sqrt(values.reduce((sum, val) => {
                    const mean = values.reduce((s, v) => s + v, 0) / values.length;
                    return sum + Math.pow(val - mean, 2);
                }, 0) / values.length)
            };
        };

        const metrics = ['projectNPV', 'capitalNPV', 'projectIRR', 'capitalIRR', 'lcoe'];
        const distributions = {};
        
        metrics.forEach(metric => {
            const allValues = scenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));
            const normalValues = normalScenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));
            const extremeValues = extremeEventScenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));

            distributions[metric] = {
                all: calculateStats(allValues),
                normal: calculateStats(normalValues),
                extreme: calculateStats(extremeValues)
            };
        });

        // Create histogram data for comparison
        const createHistogramData = (metric) => {
            const allValues = scenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));
            const normalValues = normalScenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));
            const extremeValues = extremeEventScenarios.map(s => s[metric]).filter(v => v != null && isFinite(v));

            if (allValues.length === 0) return [];

            const min = Math.min(...allValues);
            const max = Math.max(...allValues);
            const binCount = 20;
            const binSize = (max - min) / binCount;

            const bins = Array.from({length: binCount}, (_, i) => ({
                binStart: min + i * binSize,
                binEnd: min + (i + 1) * binSize,
                binCenter: min + (i + 0.5) * binSize,
                all: 0,
                normal: 0,
                extreme: 0
            }));

            allValues.forEach(value => {
                const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
                bins[binIndex].all++;
            });

            normalValues.forEach(value => {
                const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
                bins[binIndex].normal++;
            });

            extremeValues.forEach(value => {
                const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
                bins[binIndex].extreme++;
            });

            return bins;
        };

        return {
            distributions,
            histogramData: metrics.reduce((acc, metric) => {
                try {
                    acc[metric] = createHistogramData(metric);
                } catch (error) {
                    console.warn(`Failed to create histogram data for ${metric}:`, error);
                    acc[metric] = [];
                }
                return acc;
            }, {}),
            extremeEventScenarios,
            normalScenarios,
            totalScenarios: scenarios.length,
            extremeEventRate: extremeEventScenarios.length / scenarios.length
        };
    }, [simulationResults]);

    // Event category analysis
    const categoryAnalysis = useMemo(() => {
        if (!simulationResults?.extremeEventResults || simulationResults.extremeEventResults.length === 0) {
            return null;
        }

        const categoryStats = {};
        simulationResults.extremeEventResults.forEach(result => {
            const categoryId = result.extremeEvent.categoryId;
            if (!categoryStats[categoryId]) {
                categoryStats[categoryId] = {
                    count: 0,
                    totalImpact: 0,
                    averageImpact: 0,
                    events: []
                };
            }
            
            categoryStats[categoryId].count++;
            const impact = 1 - result.extremeEvent.impactMultiplier;
            categoryStats[categoryId].totalImpact += impact;
            categoryStats[categoryId].events.push(result.extremeEvent);
        });

        Object.keys(categoryStats).forEach(categoryId => {
            const stats = categoryStats[categoryId];
            stats.averageImpact = stats.totalImpact / stats.count;
        });

        return categoryStats;
    }, [simulationResults]);

    if (!processedResults) {
        return (
            <div className="p-6 text-center">
                <div className="text-gray-500 dark:text-gray-400">
                    {isRunning ? (
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span>Running extreme events simulation...</span>
                        </div>
                    ) : (
                        <div>
                            <div className="text-4xl mb-4">📊</div>
                            <h3 className="text-lg font-semibold mb-2">No Simulation Results</h3>
                            <p>Run an extreme events simulation to see comparative analysis and visualizations.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const getMetricDisplayName = (metric) => {
        const names = {
            projectNPV: 'Project NPV',
            capitalNPV: 'Capital NPV',
            projectIRR: 'Project IRR',
            capitalIRR: 'Capital IRR',
            lcoe: 'LCOE'
        };
        return names[metric] || metric;
    };

    const getCategoryDisplayName = (categoryId) => {
        const names = {
            FINANCIAL: '🏦 Financial Risk',
            STRATEGIC: '📈 Strategic Risk', 
            HAZARD: '⚡ Hazard Risk',
            OPERATIONS: '⚙️ Operations Risk',
            EXTREME_BLACK_SWAN: '🌋 Black Swan Events',
            COMPOUND: '🔗 Compound Events',
            CASCADE: '🌊 Cascading Events'
        };
        return names[categoryId] || categoryId;
    };

    const formatMetricValue = (metric, value) => {
        if (value == null || !isFinite(value) || isNaN(value)) return 'N/A';
        
        try {
            switch (metric) {
                case 'projectIRR':
                case 'capitalIRR':
                    return formatPercentage(value);
                case 'lcoe':
                    return formatCurrency(value, 4);
                default:
                    return formatCurrency(value);
            }
        } catch (error) {
            console.warn(`Error formatting metric ${metric} with value ${value}:`, error);
            return 'N/A';
        }
    };

    const renderComparisonView = () => (
        <div className="space-y-6">
            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400 mb-2">Simulation Summary</h4>
                    <div className="space-y-1 text-sm">
                        <div>Total Scenarios: {processedResults.totalScenarios.toLocaleString()}</div>
                        <div>Normal Market: {processedResults.normalScenarios.length.toLocaleString()}</div>
                        <div>Extreme Events: {processedResults.extremeEventScenarios.length.toLocaleString()}</div>
                        <div>Extreme Event Rate: {formatPercentage(processedResults.extremeEventRate * 100)}</div>
                    </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">Normal Scenarios</h4>
                    <div className="space-y-1 text-sm">
                        {processedResults.distributions[selectedMetric]?.normal ? (
                            <>
                                <div>Mean: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].normal.mean)}</div>
                                <div>Median: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].normal.median)}</div>
                                <div>95% Range: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].normal.p5)} - {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].normal.p95)}</div>
                            </>
                        ) : (
                            <div className="text-gray-500 dark:text-gray-400">No data available for this metric</div>
                        )}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">With Extreme Events</h4>
                    <div className="space-y-1 text-sm">
                        {processedResults.distributions[selectedMetric]?.all ? (
                            <>
                                <div>Mean: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].all.mean)}</div>
                                <div>Median: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].all.median)}</div>
                                <div>95% Range: {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].all.p5)} - {formatMetricValue(selectedMetric, processedResults.distributions[selectedMetric].all.p95)}</div>
                            </>
                        ) : (
                            <div className="text-gray-500 dark:text-gray-400">No data available for this metric</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Metric Selection */}
            <div className="flex flex-wrap gap-2 mb-4">
                {['projectNPV', 'capitalNPV', 'projectIRR', 'capitalIRR', 'lcoe'].map(metric => (
                    <button
                        key={metric}
                        onClick={() => setSelectedMetric(metric)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            selectedMetric === metric
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                        {getMetricDisplayName(metric)}
                    </button>
                ))}
            </div>

            {/* Distribution Comparison Chart */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold mb-4">Distribution Comparison: {getMetricDisplayName(selectedMetric)}</h4>
                {processedResults.histogramData[selectedMetric] && processedResults.histogramData[selectedMetric].length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={processedResults.histogramData[selectedMetric]}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                            <XAxis 
                                dataKey="binCenter" 
                                stroke={isDarkMode ? '#9ca3af' : '#6b7280'} 
                                tickFormatter={(value) => formatMetricValue(selectedMetric, value)}
                            />
                            <YAxis stroke={isDarkMode ? '#9ca3af' : '#6b7280'} />
                            <Tooltip 
                                formatter={(value, name) => [value, name === 'normal' ? 'Normal Market' : name === 'extreme' ? 'Extreme Events Only' : 'All Scenarios']}
                                labelFormatter={(value) => `Value: ${formatMetricValue(selectedMetric, value)}`}
                                contentStyle={{
                                    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                                    border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                                    borderRadius: '6px'
                                }}
                            />
                            <Legend />
                            <Bar dataKey="normal" fill="#10b981" name="Normal Market" />
                            <Bar dataKey="extreme" fill="#ef4444" name="Extreme Events Only" />
                        </ComposedChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
                        <div className="text-center">
                            <div className="text-4xl mb-2">📊</div>
                            <div>No chart data available for {getMetricDisplayName(selectedMetric)}</div>
                            <div className="text-sm mt-1">Run a simulation with more scenarios to generate distribution data</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderCategoryAnalysis = () => (
        <div className="space-y-6">
            {/* Category Summary */}
            {categoryAnalysis && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(categoryAnalysis).map(([categoryId, stats]) => (
                        <div key={categoryId} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <h4 className="font-semibold mb-2">{getCategoryDisplayName(categoryId)}</h4>
                            <div className="space-y-1 text-sm">
                                <div>Event Count: {stats.count}</div>
                                <div>Average Impact: {formatPercentage(stats.averageImpact * 100)}</div>
                                <div>Total Impact: {formatPercentage(stats.totalImpact * 100)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Value at Risk Analysis */}
            {simulationResults?.extremeEventStatistics?.valueAtRisk && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold mb-4">📊 Value at Risk Analysis</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">VaR 95%</div>
                            <div className="font-semibold">{formatCurrency(simulationResults.extremeEventStatistics.valueAtRisk.var95)}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">VaR 99%</div>
                            <div className="font-semibold">{formatCurrency(simulationResults.extremeEventStatistics.valueAtRisk.var99)}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Expected Shortfall 95%</div>
                            <div className="font-semibold">{formatCurrency(simulationResults.extremeEventStatistics.valueAtRisk.expectedShortfall95)}</div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Worst Case</div>
                            <div className="font-semibold text-red-600 dark:text-red-400">{formatCurrency(simulationResults.extremeEventStatistics.worstCaseScenario)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Navigation Tabs */}
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {[
                    { key: 'comparison', label: '📊 Comparison', description: 'Normal vs Extreme Events' },
                    { key: 'powerlaw', label: '📈 Power Law', description: 'Distribution Analysis & Curves' },
                    { key: 'categories', label: '🏷️ Categories', description: 'Risk Category Analysis' },
                    { key: 'scenarios', label: '🎲 Scenarios', description: 'Individual Scenario Exploration' }
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveView(tab.key)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            activeView === tab.key
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                        }`}
                        title={tab.description}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            {activeView === 'comparison' && renderComparisonView()}
            {activeView === 'powerlaw' && (
                <PowerLawChartsSubtab 
                    simulationResults={simulationResults}
                    isDarkMode={isDarkMode}
                />
            )}
            {activeView === 'categories' && (
                <RiskHeatmapSubtab 
                    simulationResults={simulationResults}
                    isDarkMode={isDarkMode}
                />
            )}
            {activeView === 'scenarios' && (
                <ScenarioExplorerSubtab 
                    simulationResults={simulationResults}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
};

export default ExtremeEventsResultsSubtab;